import React, { forwardRef, useId } from "react"

import * as Headless from "@headlessui/react"
import {
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid"
import clsx from "clsx"

import EnvVars from "~/env-vars"

import { CONTROL_HEIGHT_CLASS } from "./control"
import { Description, ErrorMessage, Field, Label } from "./fieldset"

export type MultiSelectFieldProps<T> = {
  label?: React.ReactNode
  labelSrOnly?: boolean
  description?: React.ReactNode
  warning?: React.ReactNode
  error?: React.ReactNode
  disabled?: boolean
  className?: string
  placeholder?: string
  name?: string
  options: readonly T[]
  value: T[]
  onChange: (value: T[]) => void
  getKey: (option: T) => string | number
  getLabel: (option: T) => string
  portal?: boolean
}

const IS_DEV_BUILD = EnvVars.NODE_ENV !== `production`

function MultiSelectFieldInner<T>(
  {
    label,
    labelSrOnly = false,
    description,
    warning,
    error,
    disabled,
    className,
    placeholder = `Select…`,
    name,
    options,
    value,
    onChange,
    getKey,
    getLabel,
    portal = true,
  }: MultiSelectFieldProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const controlId = useId()
  const warningId = useId()
  const errorId = useId()
  const warningMessageId = warning ? warningId : undefined
  const errorMessageId = error ? errorId : undefined

  if (IS_DEV_BUILD && !label && !labelSrOnly) {
    // eslint-disable-next-line no-console
    console.error(
      `<MultiSelectField> requires a 'label' prop, or 'labelSrOnly' must be true to acknowledge the field is intentionally unlabelled.`,
    )
  }

  return (
    <Field className={className} disabled={disabled}>
      {label ? (
        <Label className={labelSrOnly ? `sr-only` : undefined}>{label}</Label>
      ) : null}
      {description ? <Description>{description}</Description> : null}
      <Headless.Listbox
        as="div"
        data-slot="control"
        value={value}
        onChange={onChange}
        by={(left, right) => getKey(left) === getKey(right)}
        disabled={disabled}
        name={name}
        multiple
      >
        <div className="relative block w-full">
          {value.length > 0 ? (
            <div
              aria-label="Selected values"
              className="mb-2 flex flex-wrap gap-1.5"
            >
              {value.map((option) => {
                const optionLabel = getLabel(option)

                return (
                  <span
                    key={getKey(option)}
                    data-slot="chip"
                    className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 py-1 pr-1 pl-2.5 text-sm font-medium text-foreground"
                  >
                    {optionLabel}
                    <button
                      type="button"
                      aria-label={`Remove ${optionLabel}`}
                      disabled={disabled}
                      onClick={(event) => {
                        event.stopPropagation()
                        const optionKey = getKey(option)
                        onChange(
                          value.filter(
                            (candidate) => getKey(candidate) !== optionKey,
                          ),
                        )
                      }}
                      className="inline-flex size-6 items-center justify-center rounded-full text-muted hover:bg-primary/15 hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <XMarkIcon aria-hidden="true" className="size-3.5" />
                    </button>
                  </span>
                )
              })}
            </div>
          ) : null}
          <Headless.ListboxButton
            ref={ref}
            id={controlId}
            aria-invalid={error ? true : undefined}
            aria-errormessage={errorMessageId}
            data-invalid={error ? true : undefined}
            className={clsx(
              CONTROL_HEIGHT_CLASS,
              `group relative flex w-full items-center justify-between rounded-md`,
              `border border-control-border bg-background px-2.5 text-left shadow-sm`,
              `text-base/6 text-foreground sm:text-sm/6`,
              `data-hover:border-primary focus:outline-hidden`,
              `data-focus:border-primary data-focus:ring-2 data-focus:ring-focus-ring/25`,
              `data-invalid:border-danger data-hover:data-invalid:border-danger data-focus:data-invalid:ring-danger/20`,
            )}
          >
            <span className="min-w-0 flex-1 truncate">
              {value.length === 0
                ? placeholder
                : value.map(getLabel).join(`, `)}
            </span>
            <ChevronDownIcon
              aria-hidden="true"
              className="ml-2 size-4 shrink-0 text-muted transition-transform group-data-open:rotate-180"
            />
          </Headless.ListboxButton>
          <Headless.ListboxOptions
            transition
            portal={portal}
            modal={portal}
            {...(portal ? { anchor: `bottom` as const } : {})}
            className={clsx(
              portal
                ? `[--anchor-gap:10px] [--anchor-padding:--spacing(2)] isolate z-[var(--z-listbox)] w-[var(--button-width)] min-w-[var(--button-width)]`
                : `absolute inset-x-0 top-full z-[var(--z-listbox)] mt-1.5 w-full`,
              `max-h-[302px] overflow-auto rounded select-none`,
              `outline outline-transparent focus:outline-hidden`,
              `border border-primary bg-background shadow-lg`,
              `transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none`,
            )}
          >
            {options.map((option) => (
              <Headless.ListboxOption
                key={getKey(option)}
                value={option}
                className={clsx(
                  `group/option relative flex cursor-pointer items-center gap-3`,
                  `border-t border-primary/20 px-[15px] py-[15px] first:border-t-0`,
                  `text-base/6 text-foreground sm:text-sm/6`,
                  `outline-hidden select-none`,
                  `data-focus:bg-primary data-focus:text-primary-foreground`,
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    `flex size-4 shrink-0 items-center justify-center rounded border border-control-border bg-background`,
                    `group-data-selected/option:border-primary group-data-selected/option:bg-primary group-data-selected/option:text-primary-foreground`,
                    `group-data-focus/option:border-primary-foreground`,
                  )}
                >
                  <CheckIcon className="size-3 opacity-0 group-data-selected/option:opacity-100" />
                </span>
                <span className="block min-w-0 flex-1 truncate font-normal group-data-selected/option:font-semibold">
                  {getLabel(option)}
                </span>
              </Headless.ListboxOption>
            ))}
          </Headless.ListboxOptions>
        </div>
      </Headless.Listbox>
      {warning ? (
        <Description
          id={warningMessageId}
          role="status"
          className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-foreground"
        >
          {warning}
        </Description>
      ) : null}
      {error ? (
        <ErrorMessage id={errorMessageId}>{error}</ErrorMessage>
      ) : null}
    </Field>
  )
}

export const MultiSelectField = forwardRef(MultiSelectFieldInner) as <T>(
  props: MultiSelectFieldProps<T> & {
    ref?: React.ForwardedRef<HTMLButtonElement>
  },
) => React.ReactElement
